// The offline sync engine (BL-2344, ADR 066 — Offline Mode § 5): uploads the change queue, then
// pulls every synced auction's delta and the reference data into the device store, and only then
// downloads their thumbnails, resumably, while the app is in use (BUG-2124). One run at a time; a
// trigger during a run queues one follow-up, which the photo download gives way to. It never runs
// while the app is offline.
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import { isOfflineSyncEnabled } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-sync-eligibility';
import {
  isOfflineStoreFullError,
  offlineRecordBytes,
  type OfflineAuctionRecord,
  type OfflineAuctionSyncSetting,
} from '../../gateways/native-device';
import {
  applyAuctionDeltaPage,
  dropAuctionLots,
  measureAuctionBytes,
  pageChangesDevice,
  pruneUnseenLots,
  type SeenLotIds,
} from './apply-auction-delta';
import { removeDepartedLotThumbnails } from './offline-thumbnails';
import {
  createOfflinePhotoStep,
  type OfflineAuctionPhotoCount,
  type OfflinePhotoStep,
  type RunOutcome,
} from './offline-sync-photo-step';
import { createUploadPassWaiters } from './upload-pass-waiters';
import type {
  OfflineSyncedAuction,
  OfflineSyncEngine,
  OfflineSyncEngineDeps,
  OfflineSyncState,
  OfflineSyncTrigger,
} from './offline-sync-engine-types';

export type {
  OfflineSyncedAuction,
  OfflineSyncEngine,
  OfflineSyncEngineDeps,
  OfflineSyncState,
  OfflineSyncTrigger,
} from './offline-sync-engine-types';
export type { OfflineAuctionPhotoCount } from './offline-sync-photo-step';

type RunScope = { all: true } | { all: false; auctionIds: Set<string> };

/** @returns a scope covering both. */
function mergeScopes(a: RunScope | null, b: RunScope): RunScope {
  if (a == null) return b;
  if (a.all || b.all) return { all: true };
  return { all: false, auctionIds: new Set([...a.auctionIds, ...b.auctionIds]) };
}

/** @returns the scope a trigger syncs. */
function scopeOf(trigger: OfflineSyncTrigger): RunScope {
  return 'auctionId' in trigger && trigger.auctionId != null
    ? { all: false, auctionIds: new Set([trigger.auctionId]) }
    : { all: true };
}

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

/**
 * The lots whose thumbnails an auction owes once its pages are stored (BUG-2124): what it owed
 * before, plus every lot the pages wrote. A record synced before BL-2429 keeps owing all of them.
 *
 * @param existing - the auction's record before this sync, or null on a first download.
 * @param seen - the lots this sync wrote.
 * @returns the owed lot ids, or undefined for "every lot".
 */
function owedAfterPages(existing: OfflineAuctionRecord | null, seen: SeenLotIds): string[] | undefined {
  if (existing != null && existing.thumbnailRetryLotIds === undefined) return undefined;
  return [...new Set([...(existing?.thumbnailRetryLotIds ?? []), ...seen])];
}

/** @returns the record without one key (the same record when it has no such key). */
function withoutKey<T>(record: Readonly<Record<string, T>>, key: string): Readonly<Record<string, T>> {
  if (!(key in record)) return record;
  const { [key]: _gone, ...rest } = record;
  return rest;
}

/** The key the reference data is stored under (one record per scope). */
export const OFFLINE_REFERENCE_KEY = 'reference';

/**
 * @param deps - the store, the reads, the uploader, the online check, the clock.
 * @returns the engine.
 */
export function createOfflineSyncEngine(deps: OfflineSyncEngineDeps): OfflineSyncEngine {
  return new SyncEngine(deps);
}

/** The engine. A class only to keep each step its own method; nothing extends it. */
class SyncEngine implements OfflineSyncEngine {
  private state: OfflineSyncState = {
    activity: 'idle',
    lastRunFailed: false,
    storageFull: false,
    downloading: new Set(),
    settings: {},
    auctions: {},
    photos: {},
    referenceSyncedAt: null,
  };
  private readonly listeners = new Set<() => void>();
  /** Auctions switched off mid-download: the download drops them when it next checks in. */
  private readonly cancelled = new Set<string>();
  /**
   * Auctions whose first download ran out of device storage (BUG-2065). The automatic triggers
   * skip them, so a disk that is still full is not refilled and emptied every 5 minutes; Sync now
   * and the switch try again. Held for the engine's life, so a relaunch also tries once more.
   */
  private readonly outOfStorage = new Set<string>();
  /** The photo step (BUG-2124): every saved auction's owed thumbnails, after every auction's lots. */
  private readonly photoStep: OfflinePhotoStep;
  private running: Promise<void> | null = null;
  /** Set by stop(): a run checks it, with the active session, before each step that reaches the server. */
  private stopped = false;
  private queued: RunScope | null = null;
  /** Who waits for which upload pass (BUG-2111). */
  private readonly uploadPasses = createUploadPassWaiters();
  private readonly now: () => string;
  private readonly logError: (message: string, err: Error) => void;

  constructor(private readonly deps: OfflineSyncEngineDeps) {
    this.now = deps.now ?? (() => new Date().toISOString());
    // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). The run's failure also shows as "Sync failed".
    this.logError = deps.logError ?? ((message, err) => console.error(message, err));
    this.photoStep = createOfflinePhotoStep({
      store: deps.store,
      sleep: deps.sleep,
      halted: () => this.halted(),
      shouldStop: (auctionId) => this.photosShouldStop(auctionId),
      isSwitchedOn: (row) => this.isSwitchedOn(row),
      takeCancelled: (auctionId) => this.cancelled.delete(auctionId),
      dropAuction: (auctionId) => this.dropAuction(auctionId),
      publishProgress: (input) => this.publishPhotoProgress(input),
    });
  }

  getState = (): OfflineSyncState => this.state;

  stop = (): void => {
    this.stopped = true;
    // No pass will come: nobody is left waiting for one.
    this.uploadPasses.settle(Number.POSITIVE_INFINITY);
  };

  afterNextUpload = (): Promise<void> => (this.stopped ? Promise.resolve() : this.uploadPasses.next());

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  load = async (): Promise<void> => {
    const { records } = this.deps.store;
    const [settings, auctions, reference] = await Promise.all([
      records.list({ collection: 'syncSettings' }),
      records.list({ collection: 'auctions' }),
      records.get({ collection: 'reference', key: OFFLINE_REFERENCE_KEY }),
    ]);
    this.publish({
      settings: Object.fromEntries(settings.map((s) => [s.auctionId, s.enabled])),
      auctions: Object.fromEntries(
        auctions.map((a) => [a.auction.id, { auctionId: a.auction.id, bytes: a.bytes, syncedAt: a.syncedAt }]),
      ),
      referenceSyncedAt: reference?.syncedAt ?? null,
    });
  };

  run = (trigger: OfflineSyncTrigger): Promise<void> => {
    const scope = scopeOf(trigger);
    // The operator asked: an auction held back for want of storage gets another try.
    if (trigger.kind === 'sync-now' || trigger.kind === 'switch-on') {
      if (scope.all) this.outOfStorage.clear();
      else for (const auctionId of scope.auctionIds) this.outOfStorage.delete(auctionId);
      this.photoStep.clearOutOfStorage(scope.all ? 'all' : scope.auctionIds);
    }
    if (this.running) {
      this.queued = mergeScopes(this.queued, scope);
      return this.running;
    }
    const loop = async () => {
      let next: RunScope | null = scope;
      while (next) {
        this.queued = null;
        await this.runOnce(next);
        next = this.queued;
      }
    };
    this.running = loop().finally(() => {
      this.running = null;
    });
    return this.running;
  };

  setEnabled = async ({ auctionId, enabled }: { auctionId: string; enabled: boolean }): Promise<void> => {
    const setting: OfflineAuctionSyncSetting = { auctionId, enabled, changedAt: this.now() };
    await this.deps.store.records.put({ collection: 'syncSettings', key: auctionId, value: setting });
    this.publish({ settings: { ...this.state.settings, [auctionId]: enabled } });
    if (enabled) {
      this.cancelled.delete(auctionId);
      await this.run({ kind: 'switch-on', auctionId });
      return;
    }
    this.outOfStorage.delete(auctionId);
    this.photoStep.clearOutOfStorage([auctionId]);
    if (this.state.downloading.has(auctionId) || this.photoStep.isRunning(auctionId)) this.cancelled.add(auctionId);
    else await this.dropAuction(auctionId);
  };

  private publish(patch: Partial<OfflineSyncState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private setDownloading(auctionId: string, on: boolean): void {
    const downloading = new Set(this.state.downloading);
    if (on) downloading.add(auctionId);
    else downloading.delete(auctionId);
    this.publish({ downloading });
  }

  private setAuction(auctionId: string, value: OfflineSyncedAuction | null): void {
    const auctions = { ...this.state.auctions };
    if (value == null) delete auctions[auctionId];
    else auctions[auctionId] = value;
    // An auction leaving the device takes its photo count with it, in the same change.
    this.publish(value == null ? { auctions, photos: withoutKey(this.state.photos, auctionId) } : { auctions });
  }

  /** The photo step's count and running size, as one change per batch (re-renders the consumers once). */
  private publishPhotoProgress(input: { auctionId: string; photos: OfflineAuctionPhotoCount | null; bytes: number; syncedAt: string }): void {
    const { auctionId, photos, bytes, syncedAt } = input;
    this.publish({
      auctions: { ...this.state.auctions, [auctionId]: { auctionId, bytes, syncedAt } },
      photos: photos == null ? withoutKey(this.state.photos, auctionId) : { ...this.state.photos, [auctionId]: photos },
    });
  }

  private isSwitchedOn(row: AuctionListItem): boolean {
    const choice = this.state.settings[row.id];
    return isOfflineSyncEnabled({ phase: row.status, setting: choice == null ? null : { enabled: choice } });
  }

  private async dropAuction(auctionId: string): Promise<void> {
    const { records, thumbnails } = this.deps.store;
    await dropAuctionLots({ store: this.deps.store, auctionId });
    await thumbnails.deleteByAuction(auctionId);
    await records.delete({ collection: 'auctions', key: auctionId });
    this.setAuction(auctionId, null);
  }

  /**
   * True once the engine stopped or its scope is no longer the device's active session (BUG-2064).
   * Checked before each step that reaches the server, so no answer read under another session's
   * cookie is ever written into, or dropped from, this scope's store.
   */
  private async halted(): Promise<boolean> {
    if (this.stopped) return true;
    return !((await this.deps.isActiveScope?.()) ?? true);
  }

  private async runOnce(scope: RunScope): Promise<void> {
    const pass = this.uploadPasses.start();
    try {
      await this.runPass(scope, pass);
    } finally {
      // A run that skipped its upload (offline, stopped, another session) still ends its pass.
      this.uploadPasses.settle(pass);
    }
  }

  private async runPass(scope: RunScope, pass: number): Promise<void> {
    if (this.stopped || !this.deps.isOnline()) return;
    if (await this.halted()) return;
    const outcome: RunOutcome = {
      failed: false,
      storageFull: false,
      fail: (message, err) => {
        outcome.failed = true;
        if (isOfflineStoreFullError(err)) outcome.storageFull = true;
        this.logError(message, toError(err));
      },
    };
    this.publish({ activity: 'uploading' });
    try {
      await this.deps.uploader.upload({ signal: new AbortController().signal });
    } catch (err) {
      outcome.fail('offline sync: the change queue upload failed', err);
    }
    // The queue has had its turn: a screen waiting to re-read the server reads it now (BUG-2111).
    this.uploadPasses.settle(pass);
    if (await this.halted()) {
      this.publish({ activity: 'idle' });
      return;
    }
    this.publish({ activity: 'downloading' });
    try {
      const listed = await this.deps.api.listAuctions();
      if (await this.halted()) {
        this.publish({ activity: 'idle' });
        return;
      }
      const targets = await this.reconcile(listed);
      // BUG-2124: every auction's lots first, then the reference data, then the photos.
      const lotsFailed = await this.syncAllLots({ targets, scope, outcome });
      // A scoped run (one auction's switch) leaves the reference poll to the full runs.
      if (scope.all && !(await this.halted())) await this.syncReference();
      await this.photoStep.run({ targets, lotsFailed, outcome });
    } catch (err) {
      outcome.fail('offline sync: the run failed', err);
    }
    this.publish({ activity: 'idle', lastRunFailed: outcome.failed, storageFull: outcome.storageFull });
  }

  /** Drop what should no longer be on the device, and return the auctions to download. */
  private async reconcile(listed: AuctionListItem[]): Promise<AuctionListItem[]> {
    const enabled = listed.filter((row) => this.isSwitchedOn(row));
    const keep = new Set(enabled.map((row) => row.id));
    for (const record of await this.deps.store.records.list({ collection: 'auctions' })) {
      if (!keep.has(record.auction.id)) await this.dropAuction(record.auction.id);
    }
    return enabled;
  }

  /**
   * The lots step: each target's delta pages, one auction after another.
   *
   * @returns the auctions whose lot read failed this run: their photos wait for a run that reads them.
   */
  private async syncAllLots(input: { targets: AuctionListItem[]; scope: RunScope; outcome: RunOutcome }): Promise<Set<string>> {
    const { targets, scope, outcome } = input;
    const failed = new Set<string>();
    for (const row of targets) {
      if (await this.halted()) break;
      // Re-read the switch: it may have been turned off while earlier auctions downloaded.
      if (!this.isSwitchedOn(row) || (!scope.all && !scope.auctionIds.has(row.id))) continue;
      if (this.outOfStorage.has(row.id)) {
        // Still not on the device: the run reports the full disk rather than a clean sync.
        outcome.failed = true;
        outcome.storageFull = true;
        continue;
      }
      try {
        await this.syncAuction(row);
      } catch (err) {
        failed.add(row.id);
        outcome.fail(`offline sync: auction ${row.id} failed`, err);
      }
    }
    return failed;
  }

  /**
   * Download one auction's pages until the server has nothing more, then save it: lots, cursor,
   * last sync, size and the lots whose thumbnails are owed (BUG-2124). Its photos come later, in
   * the photo step, so a run cut short after this keeps the auction and resumes from its cursor.
   *
   * @returns once the auction is saved, dropped, or the run halted.
   */
  private async syncAuction(listRow: AuctionListItem): Promise<void> {
    const { store, api } = this.deps;
    const auctionId = listRow.id;
    // Marked downloading before the first await, so a switch-off in any window of the run is
    // seen as a download and dropped when the run checks in.
    this.setDownloading(auctionId, true);
    let existing: OfflineAuctionRecord | null = null;
    let completed = false;
    try {
      existing = await store.records.get({ collection: 'auctions', key: auctionId });
      let cursor = existing?.cursor ?? null;
      const seen: SeenLotIds = new Set();
      let full = false;
      let changed = false;
      let removedLots = false;
      let bytes = existing?.bytes ?? 0;
      for (;;) {
        const read = await api.readDelta({ auctionId, cursor });
        if (this.cancelled.has(auctionId)) return;
        // BUG-2064: an answer read after a stop or under another session is never applied, and a
        // "gone" read under it never drops this scope's copy of the auction.
        if (await this.halted()) return;
        if (read.kind === 'gone' || !read.delta.auction.eligible) {
          await this.dropAuction(auctionId);
          return;
        }
        const { delta } = read;
        if (delta.mode === 'full') full = true;
        await applyAuctionDeltaPage({ store, auctionId, delta, seen });
        if (pageChangesDevice(delta)) changed = true;
        if (delta.tombstones.length > 0) removedLots = true;
        // A running estimate while pages arrive; the exact size is measured once, at the end.
        bytes += delta.lots.reduce((sum, lot) => sum + offlineRecordBytes(lot), 0);
        this.setAuction(auctionId, { auctionId, bytes, syncedAt: existing?.syncedAt ?? null });
        cursor = delta.cursor;
        if (delta.hasMore) continue;
        if (this.cancelled.has(auctionId)) return;
        if (full && (await pruneUnseenLots({ store, auctionId, seen })) > 0) removedLots = true;
        // A removal owes no photo pass, so its thumbnails go now. The thumbnails' aggregate on the
        // record stands unless photos went, so a poll never re-measures them (BL-2609).
        const removedThumbnails = removedLots ? await removeDepartedLotThumbnails({ store, auctionId }) : 0;
        let thumbnails = existing?.thumbnails;
        if (changed || existing == null || removedThumbnails > 0) {
          const size = await measureAuctionBytes({ store, auctionId, thumbnails: removedThumbnails > 0 ? undefined : thumbnails });
          bytes = size.bytes;
          thumbnails = size.thumbnails;
        }
        const syncedAt = this.now();
        await store.records.put({
          collection: 'auctions',
          key: auctionId,
          value: { auction: delta.auction, listRow, cursor, syncedAt, bytes, thumbnails, thumbnailRetryLotIds: owedAfterPages(existing, seen) },
        });
        this.setAuction(auctionId, { auctionId, bytes, syncedAt });
        completed = true;
        return;
      }
    } catch (err) {
      // A first download the disk cannot hold would fail the same way on every poll (BUG-2065).
      if (existing == null && isOfflineStoreFullError(err)) this.outOfStorage.add(auctionId);
      throw err;
    } finally {
      this.setDownloading(auctionId, false);
      if (this.cancelled.delete(auctionId)) {
        await this.dropAuction(auctionId);
      } else if (!completed && existing == null) {
        // A first download cut short leaves lots with no auction record, which no later reconcile
        // would find: drop them now. Its thumbnails are kept (BUG-2124 AC 5).
        await dropAuctionLots({ store, auctionId });
        this.setAuction(auctionId, null);
      }
    }
  }

  /** True once the photo download should give way: checked between its batches (BUG-2124). */
  private photosShouldStop(auctionId: string): boolean {
    return (
      this.stopped ||
      this.cancelled.has(auctionId) ||
      this.queued != null ||
      !this.deps.isOnline() ||
      !(this.deps.isInUse?.() ?? true)
    );
  }

  private async syncReference(): Promise<void> {
    const { records } = this.deps.store;
    const stored = await records.get({ collection: 'reference', key: OFFLINE_REFERENCE_KEY });
    const read = await this.deps.api.readReference({ etag: stored?.etag ?? null });
    if (await this.halted()) return;
    const syncedAt = this.now();
    if (read.kind === 'changed') {
      await records.put({
        collection: 'reference',
        key: OFFLINE_REFERENCE_KEY,
        value: { reference: read.reference, etag: read.etag, syncedAt },
      });
    } else if (stored != null) {
      await records.put({ collection: 'reference', key: OFFLINE_REFERENCE_KEY, value: { ...stored, syncedAt } });
    }
    this.publish({ referenceSyncedAt: syncedAt });
  }
}
